import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { requestGetPromotionProductsRecommend } from '@apis/renewal/promotion';
import { useWckQueries } from './wckQuery/useWckQueries';
import { useWckQuery } from './wckQuery/useWckQuery';

interface UsePromotionProductsRecommendParams {
  promotionId: number;
  contentId: number;
  moduleId: number;
  recommendItems: string[];
}

interface UsePromotionProductsRecommendOptions {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
}

/**
 * 프로모션 추천 상품 목록 조회 훅
 */
export const usePromotionProductsRecommend = (
  params: UsePromotionProductsRecommendParams,
  options: UsePromotionProductsRecommendOptions = {}
) => {
  const { enabled = true, refetchOnMount = true } = options;

  return useWckQuery({
    queryKey: [
      'promotionProductsRecommend',
      params.promotionId,
      params.contentId,
      params.moduleId,
    ],
    queryFn: () => requestGetPromotionProductsRecommend(params),
    enabled,
    refetchOnMount,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000 * 5, // 5분간 stale하지 않음
    gcTime: 60 * 1000 * 10, // 10분간 캐시 유지
    initialDataEnabled: true, // 초기 데이터 활성화
  });
};

/**
 * 프로모션 추천 상품 목록 조회 (무한 스크롤용)
 */
export const usePromotionProductsRecommendInfinite = (
  params: Omit<UsePromotionProductsRecommendParams, 'page'>,
  options: UsePromotionProductsRecommendOptions = {}
) => {
  const {
    enabled = true,
    staleTime = 60 * 1000 * 5, // 5분
    cacheTime = 60 * 1000 * 10, // 10분
    refetchOnWindowFocus = false,
    refetchOnMount = true,
  } = options;

  return useWckQuery({
    queryKey: [
      'promotionProductsRecommend',
      params.promotionId,
      params.contentId,
      params.moduleId,
      params.recommendItems,
    ],
    queryFn: () =>
      requestGetPromotionProductsRecommend({
        ...params,
      }),
    enabled: enabled && params.recommendItems.length > 0,
    staleTime,
    gcTime: cacheTime,
    refetchOnWindowFocus,
    refetchOnMount,
    placeholderData: keepPreviousData,
  });
};
